import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Button,
  TextField,
  Select,
  MenuItem,
  Typography,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress
} from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ClearIcon from "@mui/icons-material/Clear";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CodeIcon from "@mui/icons-material/Code";
import HttpIcon from "@mui/icons-material/Http";
import SecurityIcon from "@mui/icons-material/Security";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SendIcon from "@mui/icons-material/Send";
import DoneIcon from "@mui/icons-material/Done";
import { 
  Grid,
  Divider, 
  Paper, 
  IconButton, 
  Chip, 
  FormControl, 
  InputLabel, 
  Tooltip, 
  Alert 
} from "@mui/material";
import GraphApiTemplatesComponent from "./extensions/GraphApiTemplatesComponent";
import { HttpMethod } from "./extensions/GraphApiTemplates"; // Import the HttpMethod type

interface GraphExplorerProps {
  accessToken: string;
  onBack: () => void;
  isReadOnly?: boolean; // New prop to enforce read-only mode
}

/**
 * A small dictionary of Graph endpoints => required delegated scopes,
 * potentially also distinguishing between read vs. write (by method).
 */
const endpointScopeMap: Array<{
  match: RegExp;               // regex to see if query matches (e.g. /users, /me/calendar)
  readScopes: string[];        // scopes for read-only operations
  writeScopes?: string[];      // scopes for write operations
  requiresAdmin?: boolean;     // if the endpoint definitely requires admin privileges
}> = [
  // More specific /me subpaths should come BEFORE the generic /me matcher
  {
    match: /^\/me\/messages/i,
    readScopes: ["Mail.Read"],
    requiresAdmin: false
  },
  // /users ...
  {
    match: /^\/users/i,
    readScopes: ["User.Read.All"],
    writeScopes: ["User.ReadWrite.All"],
    requiresAdmin: true // Usually "User.ReadWrite.All" requires Admin
  },
  // /groups ...
  {
    match: /^\/groups/i,
    readScopes: ["Group.Read.All"],
    writeScopes: ["Group.ReadWrite.All"],
    requiresAdmin: true
  },
  // /applications ...
  {
    match: /^\/applications/i,
    readScopes: ["Application.Read.All"],
    // Note: For read operations, Application.Read.All is sufficient.
    // Write operations (not used in read-only mode) would require one of the ReadWrite variants.
    writeScopes: ["Application.ReadWrite.OwnedBy", "Application.ReadWrite.All"],
    requiresAdmin: true
  },
  // /organization ...
  {
    match: /^\/organization/i,
    readScopes: ["Organization.Read.All"],
    requiresAdmin: true
  },
  // /auditLogs ...
  {
    match: /^\/auditLogs/i,
    readScopes: ["AuditLog.Read.All"],
    requiresAdmin: true
  },
  // /messages ...
  {
    match: /^\/messages/i,
    readScopes: ["Mail.Read.All"],
    requiresAdmin: false
  },
  // Generic /me catch-all LAST so specific routes are matched first
  {
    match: /^\/me/i,
    readScopes: ["User.Read"],
  },
  
  // Identity Protection
  {
    match: /^\/identityProtection\/riskDetections/i,
    readScopes: ["IdentityRiskyUser.Read.All"],
    requiresAdmin: true
  },
  {
    match: /^\/identityProtection\/riskyUsers/i,
    readScopes: ["IdentityRiskyUser.Read.All"],
    writeScopes: ["IdentityRiskyUser.ReadWrite.All"],
    requiresAdmin: true
  },
  
  // Conditional Access
  {
    match: /^\/identity\/conditionalAccess\/policies/i,
    readScopes: ["Policy.Read.All"],
    writeScopes: ["Policy.ReadWrite.ConditionalAccess"],
    requiresAdmin: true
  },
  {
    match: /^\/identity\/conditionalAccess\/namedLocations/i,
    readScopes: ["Policy.Read.All"],
    writeScopes: ["Policy.ReadWrite.ConditionalAccess"],
    requiresAdmin: true
  },
  {
    match: /^\/identity\/conditionalAccess\/authenticationStrengths/i,
    readScopes: ["Policy.Read.All"],
    writeScopes: ["Policy.ReadWrite.ConditionalAccess"],
    requiresAdmin: true
  },
  
  // Authentication Methods
  {
    match: /^\/users\/.*\/authentication\/methods/i,
    readScopes: ["UserAuthenticationMethod.Read.All"],
    writeScopes: ["UserAuthenticationMethod.ReadWrite.All"],
    requiresAdmin: true
  },
  {
    match: /^\/reports\/authenticationMethods/i,
    readScopes: ["Reports.Read.All"],
    requiresAdmin: true
  },
  {
    match: /^\/policies\/authenticationMethodsPolicy/i,
    readScopes: ["Policy.Read.All"],
    writeScopes: ["Policy.ReadWrite.AuthenticationMethod"],
    requiresAdmin: true
  },
  
  // Directory Settings
  {
    match: /^\/settings/i,
    readScopes: ["Directory.Read.All"],
    writeScopes: ["Directory.ReadWrite.All"],
    requiresAdmin: true
  },
  
  // Service Principals
  {
    match: /^\/servicePrincipals/i,
    readScopes: ["Application.Read.All"],
    writeScopes: ["Application.ReadWrite.All"],
    requiresAdmin: true
  }

  // If you expand for /drive, /sites, etc., add them below ...
];

/** Helper to decode a JWT for scope inspection. */
function decodeJwt(token: string): any {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64Url = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64Url)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Failed to decode token:", e);
    return null;
  }
}

/**
 * Given a Graph endpoint + method, attempt to identify which delegated scopes are required.
 * For advanced usage, you'd merge in user-specified scopes or handle partial overlaps.
 */
function detectNeededScopes(query: string, method: HttpMethod): {
  possibleScopes: string[];
  definitelyRequiresAdmin: boolean;
} {
  const lower = query.toLowerCase().trim();
  const matchedItem = endpointScopeMap.find((item) => item.match.test(lower));
  if (!matchedItem) {
    // Smarter fallback based on URL patterns
    if (lower.includes('/me/')) {
      // If it contains /me/ but we don't have a specific mapping, it's likely
      // a personal endpoint that needs at least User.Read
      return {
        possibleScopes: ["User.Read"],
        definitelyRequiresAdmin: false
      };
    } else if (lower.includes('/identity/') || lower.includes('protection')) {
      // Identity-related endpoints often need admin access
      return {
        possibleScopes: ["Directory.Read.All", "Policy.Read.All"],
        definitelyRequiresAdmin: true
      };
    } else if (lower.includes('/policies/') || lower.includes('/policy/')) {
      // Policy-related endpoints
      return {
        possibleScopes: ["Policy.Read.All"],
        definitelyRequiresAdmin: true
      };
    } else if (lower.includes('/directory')) {
      // Directory-related endpoints
      return {
        possibleScopes: ["Directory.Read.All"],
        definitelyRequiresAdmin: true
      };
    } else {
      // Generic fallback - warn that we couldn't detect specific scopes
      console.warn(`Could not detect specific scopes for endpoint: ${query}`);
      return {
        possibleScopes: ["User.Read", "Directory.Read.All"],
        definitelyRequiresAdmin: method !== "GET" // Assume write operations need admin
      };
    }
  }

  // If method is GET => use readScopes
  if (method === "GET") {
    return {
      possibleScopes: matchedItem.readScopes,
      definitelyRequiresAdmin: matchedItem.requiresAdmin ?? false
    };
  } else {
    // For POST/PATCH/DELETE => we attempt writeScopes if available, else fallback to read
    return {
      possibleScopes: matchedItem.writeScopes ?? matchedItem.readScopes,
      definitelyRequiresAdmin: matchedItem.requiresAdmin ?? false
    };
  }
}

export const GraphExplorer: React.FC<GraphExplorerProps> = ({ 
  accessToken, 
  onBack, 
  isReadOnly = false 
}) => {
  // ========================
  // 1) MSAL & State
  // ========================
  const { instance, accounts, inProgress } = useMsal();
  // inProgress can be 'login', 'acquireToken', 'handleRedirect', or 'none'

  // CORE states from your original code:
  const [query, setQuery] = useState<string>("/me");
  const [method, setMethod] = useState<HttpMethod>(isReadOnly ? "GET" : "GET");
  const [body, setBody] = useState("");
  const [customHeaders, setCustomHeaders] = useState("");
  const [response, setResponse] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [tabIndex, setTabIndex] = useState<number>(0);

  // For dynamic scopes/consent:
  const [currentAccessToken, setCurrentAccessToken] = useState<string>(accessToken);
  const [neededScopes, setNeededScopes] = useState<string[]>([]);
  const [missingScopes, setMissingScopes] = useState<string[]>([]);
  const [requiresAdmin, setRequiresAdmin] = useState<boolean>(false);
  // We'll also track a small "isRequestingConsent" to prevent double popups.
  const [isRequestingConsent, setIsRequestingConsent] = useState<boolean>(false);

  const [copySuccess, setCopySuccess] = useState<boolean>(false);
    /**
   * Handler for applying a selected template from the GraphApiTemplatesComponent
   */
  const handleApplyTemplate = (
    templateMethod: HttpMethod,
    templateEndpoint: string,
    templateBody: string,
    templateHeaders: string
  ) => {
    // Enforce GET in read-only mode to avoid requesting write scopes
    setMethod(isReadOnly ? "GET" as HttpMethod : templateMethod);
    setQuery(templateEndpoint);
    
    // Set the body if not GET/DELETE
    if (templateMethod !== "GET" && templateMethod !== "DELETE") {
      setBody(templateBody);
    }
    
    // Set headers if provided
    try {
      const parsedHeaders = JSON.parse(templateHeaders);
      if (Object.keys(parsedHeaders).length > 0) {
        setCustomHeaders(templateHeaders);
      }
    } catch (e) {
      console.error("Error parsing template headers:", e);
    }
    
    // Reset response and errors
    setResponse("");
    setErrorMessage("");
    
    // Update the scopes based on the new endpoint. Force GET in read-only mode
    // so the template can never surface a write scope into the hints.
    const effectiveMethod: HttpMethod = isReadOnly ? "GET" : templateMethod;
    const { possibleScopes, definitelyRequiresAdmin } = detectNeededScopes(templateEndpoint, effectiveMethod);
    setNeededScopes(possibleScopes);
    setRequiresAdmin(definitelyRequiresAdmin);
    
    // The missing scopes will be recalculated by the useEffect that depends on neededScopes
  };

  // Override method setter in read-only mode
  const handleMethodChange = (newMethod: HttpMethod) => {
    if (isReadOnly && newMethod !== "GET") {
      console.warn("Write operations disabled in read-only mode");
      return;
    }
    setMethod(newMethod);
  };  

  // Add this effect to handle changes to the accessToken prop
  useEffect(() => {
    if (accessToken && accessToken !== currentAccessToken) {
      setCurrentAccessToken(accessToken);
    }
  }, [accessToken, currentAccessToken]);

  // Add this helper function as well
  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // (A) Recompute “needed scopes” each time query/method changes.
  // In read-only mode we always evaluate scopes against GET, so write scopes
  // (e.g. *.ReadWrite.*) can never bubble up into the consent hints — even if
  // an upstream regression were to leak a non-GET method into state.
  useEffect(() => {
    const scopeMethod: HttpMethod = isReadOnly ? "GET" : method;
    const { possibleScopes, definitelyRequiresAdmin } = detectNeededScopes(query, scopeMethod);
    setNeededScopes(possibleScopes);
    setRequiresAdmin(definitelyRequiresAdmin);
  }, [query, method, isReadOnly]);

  // (B) Recompute “missing scopes” each time the current token or neededScopes changes
  useEffect(() => {
    if (!currentAccessToken) {
      setMissingScopes(neededScopes);
      return;
    }
    if (neededScopes.length === 0) {
      setMissingScopes([]);
      return;
    }

    const decoded = decodeJwt(currentAccessToken);
    if (!decoded?.scp) {
      // If no “scp” claim at all => everything is missing
      setMissingScopes(neededScopes);
      return;
    }

    const tokenScopes = decoded.scp.split(" ");
    const notGranted = neededScopes.filter((s) => !tokenScopes.includes(s));
    setMissingScopes(notGranted);
  }, [currentAccessToken, neededScopes]);

  // ========================
  // 2) Tabs & UI events
  // ========================
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  // The main “Run Query” logic, now uses `currentAccessToken`
  const handleRunQuery = async () => {
    setResponse("");
    setErrorMessage("");

    const url = `https://graph.microsoft.com/v1.0${query}`;

    // Defense in depth: re-assert the read-only invariant at the fetch site.
    // Even if `method` state were somehow set to a write verb, in read-only
    // mode we coerce the outgoing request to GET — guaranteeing no PATCH /
    // POST / PUT / DELETE ever leaves the browser bound for Microsoft Graph.
    const effectiveMethod: HttpMethod = isReadOnly ? "GET" : method;

    let parsedHeaders: Record<string, string> = {};
    if (customHeaders.trim()) {
      try {
        parsedHeaders = JSON.parse(customHeaders);
      } catch (err) {
        setErrorMessage("Invalid JSON in custom headers.");
        return;
      }
    }

    const buildOptions = (token: string): RequestInit => {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...parsedHeaders
      };
      const fetchOptions: RequestInit = {
        method: effectiveMethod,
        headers
      };
      if (effectiveMethod !== "GET" && effectiveMethod !== "DELETE" && body) {
        try {
          const parsedBody = JSON.parse(body);
          fetchOptions.body = JSON.stringify(parsedBody);
        } catch (err: any) {
          console.error("Invalid JSON body:", err);
          setErrorMessage("Invalid JSON in request body.");
          throw err;
        }
      }
      return fetchOptions;
    };

    const runOnce = async (token: string): Promise<Response> => {
      const res = await fetch(url, buildOptions(token));
      const statusLine = `Status: ${res.status} ${res.statusText}\n`;
      let responseText = await res.text();

      try {
        const data = JSON.parse(responseText);
        responseText = JSON.stringify(data, null, 2);
      } catch {
        // keep as raw if not valid JSON
      }

      const combinedResponse = `${statusLine}\n${responseText}`;
      setResponse(combinedResponse);
      return res;
    };

    const tryAutoConsentAndRetry = async (): Promise<boolean> => {
      // Only attempt auto-consent if we actually detected missing scopes
      if (!missingScopes.length) return false;

      // Avoid double popups or conflicts
      if (inProgress !== "none" || isRequestingConsent) return false;

      // If no account, we can't acquire tokens
      if (!accounts || accounts.length === 0) {
        setErrorMessage("No signed-in account found. Please sign in again.");
        return false;
      }

      try {
        setIsRequestingConsent(true);
        const popupRequest: any = {
          scopes: missingScopes,
          account: accounts[0],
          prompt: "consent",
          extraQueryParameters: requiresAdmin ? { prompt: "admin_consent" } : undefined,
        };
        const popupResult = await instance.acquireTokenPopup(popupRequest);
        if (popupResult?.accessToken) {
          setCurrentAccessToken(popupResult.accessToken);
          // Retry once automatically with the new token
          const retryRes = await runOnce(popupResult.accessToken);
          if (!retryRes.ok) {
            // Even after consent, still failing
            setErrorMessage(`Request failed after consent (HTTP ${retryRes.status}). See response for details.`);
          }
          return true;
        }
      } catch (err: any) {
        console.error("Auto-consent failed:", err);
        if (err?.errorCode === "interaction_in_progress") {
          setErrorMessage("Another authentication popup is in progress. Please complete it and try again.");
        } else if (err instanceof InteractionRequiredAuthError) {
          setErrorMessage("Interactive authentication required or was cancelled. Try again or use the Permissions tab.");
        } else {
          setErrorMessage(`Consent failed: ${err?.message || err}`);
        }
      } finally {
        setIsRequestingConsent(false);
      }
      return false;
    };

    try {
      const initialRes = await runOnce(currentAccessToken);
      if (!initialRes.ok && (initialRes.status === 401 || initialRes.status === 403)) {
        // Parse response to enhance error messages and decide if we should auto-consent
        let errorBody: any = null;
        try {
          errorBody = JSON.parse(initialRes ? await initialRes.clone().text() : "");
        } catch {
          // ignore
        }

        const errorCode = errorBody?.error?.code || '';
        const errMsg = errorBody?.error?.message || '';

        // Attempt auto-consent+retry only if missing scopes are detected
        const didRetry = await tryAutoConsentAndRetry();
        if (!didRetry) {
          if (errorCode === 'Authorization_RequestDenied' || errMsg.toLowerCase().includes('permission')) {
            setErrorMessage(
              `Permission error (HTTP ${initialRes.status}). You may be missing required scopes. ` +
              `Open the Permissions tab and click "Grant Missing Permissions" to request consent.`
            );
          } else {
            setErrorMessage(
              `Authentication/authorization error (HTTP ${initialRes.status}). ` +
              `You may need additional scopes or admin consent. See Permissions tab.`
            );
          }
        }
      }
    } catch (error: any) {
      console.error("[GraphExplorer] Error in handleRunQuery:", error);
      setResponse(`Request failed. See console for details.\n\n${error.message}`);
    }
  };

  /**
   * Request consent for any missing scopes using MSAL's popup flow.
   * If the user is not an admin, admin-only scopes won't succeed unless
   * they've previously been granted tenant-wide by an admin.
   */
  async function requestConsentForMissingScopes() {
    if (!missingScopes.length) {
      alert("No missing scopes to request consent for!");
      return;
    }
  
    // 1) If MSAL is still in the middle of something, bail out
    if (inProgress !== "none") {
      alert("Another interactive flow is in progress. Please wait for it to complete.");
      return;
    }
  
    // 2) If we've already started the popup, don't start again
    if (isRequestingConsent) {
      alert("Consent is already in progress. Please wait.");
      return;
    }
  
    setIsRequestingConsent(true);
  
    // 3) If you need admin consent, we rely on extraQueryParameters
    const request = {
      scopes: missingScopes,
      account: accounts[0],
      prompt: "consent", // valid MSAL prompt
      extraQueryParameters: requiresAdmin
        ? { prompt: "admin_consent" }
        : undefined,
    };
  
    try {
      const response = await instance.acquireTokenPopup(request);
      if (response?.accessToken) {
        setCurrentAccessToken(response.accessToken);
        alert("Consent successful! The new token includes additional scopes. You can Run Query again.");
      }
    } catch (err: any) {
      console.error("Consent request failed:", err);
  
      if (err.errorCode === "interaction_in_progress") {
        alert("Another popup is already open. Please close it first, or wait.");
      } else if (err instanceof InteractionRequiredAuthError) {
        alert("User canceled or additional steps are needed. Check console for details.");
      } else {
        alert(`Consent request failed: ${err.message || err}`);
      }
    } finally {
      setIsRequestingConsent(false);
    }
  }
  

  // ========================
  // 3) Render
  // ========================
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                borderColor: "primary.main",
                color: "primary.main"
              }
            }}
          >
            Back
          </Button>

          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              Graph API Reader
              {isReadOnly && <Chip label="Read-Only" size="small" sx={{ ml: 2 }} />}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              {isReadOnly 
                ? "View Microsoft Graph API endpoints with your current access token" 
                : "Test Microsoft Graph API endpoints with your current access token"
              }
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Request Configuration */}
        <Grid item xs={12}>
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
          <CardContent sx={{ p: 0 }}>
            <Typography
              variant="h6"
              sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid rgba(0,0,0,0.12)',
                backgroundColor: '#f9f9f9',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <HttpIcon sx={{ mr: 1, color: 'primary.main' }} />
              Request Builder
            </Typography>

              <Box sx={{ p: 3 }}>
                {/* API Templates Component */}
                <GraphApiTemplatesComponent 
                  onApplyTemplate={handleApplyTemplate} 
                  isReadOnly={isReadOnly} 
                />
                
                <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                  <TextField
                    label="Graph API Endpoint"
                    placeholder="Example: /me or /users"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setResponse("");
                      setErrorMessage("");
                    }}
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#ffffff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 600,
                        color: 'primary.main'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#c7d8f2',
                        borderWidth: 1.5
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <Box 
                          component="span"
                          sx={{
                            backgroundColor: '#e6f0ff',
                            color: 'primary.main',
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            px: 1,
                            py: 0.5,
                            mr: 1,
                            borderRadius: 1,
                            border: '1px solid #c7d8f2'
                          }}
                        >
                          v1.0
                        </Box>
                      ),
                      endAdornment: query && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setQuery("");
                            setResponse("");
                            setErrorMessage("");
                          }}
                        >
                          <ClearIcon />
                        </IconButton>
                      )
                    }}
                  />

                  <FormControl sx={{ 
                    minWidth: 120,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#ffffff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    },
                  }}>
                    <InputLabel id="http-method-label">Method</InputLabel>
                    <Select
                      labelId="http-method-label"
                      value={method}
                      label="Method"
                      onChange={(e) => handleMethodChange(e.target.value as HttpMethod)}
                      disabled={isReadOnly}
                      sx={{
                        borderRadius: 2,
                        height: '56px',
                        fontWeight: 500
                      }}
                    >
                      <MenuItem value="GET">GET</MenuItem>
                      {!isReadOnly && (
                        <>
                          <MenuItem value="POST">POST</MenuItem>
                          <MenuItem value="PATCH">PATCH</MenuItem>
                          <MenuItem value="DELETE">DELETE</MenuItem>
                        </>
                      )}
                    </Select>
                  </FormControl>
                </Box>

                {/* REMOVED THE MISPLACED "Graph API Reader" HEADER HERE */}

                <Box sx={{ mb: 3 }}>
                  <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      mb: 2,
                      borderBottom: 1,
                      borderColor: 'divider',
                      '& .MuiTab-root': {
                        minWidth: 'auto',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        px: 2,
                      }
                    }}
                  >
                    <Tab label="Request Body" icon={<CodeIcon />} iconPosition="start" disabled={method === "GET" || method === "DELETE"} />
                    <Tab label="Headers" icon={<HttpIcon />} iconPosition="start" />
                    <Tab label="Permissions" icon={<SecurityIcon />} iconPosition="start" />
                    <Tab label="Access Token" icon={<VpnKeyIcon />} iconPosition="start" />
                  </Tabs>

                  {/* 1) Body Tab */}
                  {tabIndex === 0 && (
                    <Box>
                      {isReadOnly ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          Request Body is disabled in read-only mode. Only GET operations are allowed.
                        </Alert>
                      ) : method === "GET" || method === "DELETE" ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          Request Body is disabled for GET/DELETE methods.
                        </Alert>
                      ) : (
                        <TextField
                          placeholder='{"key": "value"}'
                          multiline
                          minRows={8}
                          value={body}
                          onChange={(e) => setBody(e.target.value)}
                          fullWidth
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              borderRadius: 2,
                            }
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {/* 2) Headers Tab */}
                  {tabIndex === 1 && (
                    <Box>
                      <TextField
                        placeholder='{"ConsistencyLevel": "eventual", "Prefer": "return=representation"}'
                        multiline
                        minRows={8}
                        value={customHeaders}
                        onChange={(e) => setCustomHeaders(e.target.value)}
                        fullWidth
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            borderRadius: 2,
                          }
                        }}
                      />
                      
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                        Headers should be in valid JSON format with quotes around keys and values.
                        Example: {"{\"ConsistencyLevel\": \"eventual\"}"}
                      </Typography>
                      
                      {/* Add quick buttons for common headers */}
                      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Button 
                          variant="outlined" 
                          size="small"
                          sx={{ borderRadius: 2 }}
                          onClick={() => {
                            try {
                              // Parse existing headers or create empty object
                              const existingHeaders = customHeaders.trim() ? 
                                JSON.parse(customHeaders) : {};
                              
                              // Add ConsistencyLevel header
                              const updatedHeaders = {
                                ...existingHeaders,
                                "ConsistencyLevel": "eventual"
                              };
                              
                              // Update headers field with formatted JSON
                              setCustomHeaders(JSON.stringify(updatedHeaders, null, 2));
                            } catch (err) {
                              // If current headers aren't valid JSON, just set new ones
                              setCustomHeaders(JSON.stringify({
                                "ConsistencyLevel": "eventual"
                              }, null, 2));
                            }
                          }}
                        >
                          + Add ConsistencyLevel: eventual
                        </Button>
                        
                        <Button 
                          variant="outlined" 
                          size="small"
                          sx={{ borderRadius: 2 }}
                          onClick={() => {
                            try {
                              const existingHeaders = customHeaders.trim() ? 
                                JSON.parse(customHeaders) : {};
                              
                              const updatedHeaders = {
                                ...existingHeaders,
                                "Prefer": "return=representation"
                              };
                              
                              setCustomHeaders(JSON.stringify(updatedHeaders, null, 2));
                            } catch (err) {
                              setCustomHeaders(JSON.stringify({
                                "Prefer": "return=representation"
                              }, null, 2));
                            }
                          }}
                        >
                          + Add Prefer: return=representation
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* 3) Permissions Tab */}
                  {tabIndex === 2 && (
                    <Box sx={{ p: 2, backgroundColor: "#f9f9f9", borderRadius: 2 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Required Permissions
                      </Typography>

                      {neededScopes.length === 0 ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          No specific scopes detected for this endpoint.
                        </Alert>
                      ) : (
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1.5 }}>
                            <strong>Detected scopes:</strong>
                          </Typography>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {neededScopes.map(scope => (
                              <Chip
                                key={scope}
                                label={scope}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />
                            ))}
                          </Box>

                          {missingScopes.length > 0 ? (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Typography variant="body2" sx={{ mb: 1.5, color: 'warning.dark', fontWeight: 500 }}>
                                <strong>Missing scopes:</strong>
                              </Typography>

                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {missingScopes.map(scope => (
                                  <Chip
                                    key={scope}
                                    label={scope}
                                    color="warning"
                                    variant="outlined"
                                    size="small"
                                  />
                                ))}
                              </Box>

                              {requiresAdmin && (
                                <Alert severity="warning" sx={{ mt: 2, mb: 2, borderRadius: 2 }}>
                                  Admin privileges required for these scopes
                                </Alert>
                              )}

                              <Button
                                variant="contained"
                                color="primary"
                                sx={{ mt: 1, borderRadius: 2 }}
                                disabled={isRequestingConsent}
                                onClick={requestConsentForMissingScopes}
                                startIcon={isRequestingConsent ? <CircularProgress size={16} /> : <SecurityIcon />}
                              >
                                {isRequestingConsent ? "Requesting..." : "Grant Missing Permissions"}
                              </Button>
                            </>
                          ) : (
                            <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                              All required scopes are already granted!
                            </Alert>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* 4) Access Token Tab */}
                  {tabIndex === 3 && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Current Access Token
                        </Typography>

                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={copySuccess ? <DoneIcon /> : <ContentCopyIcon />}
                          onClick={() => handleCopyToClipboard(currentAccessToken)}
                          sx={{
                            borderRadius: 2,
                            color: copySuccess ? 'success.main' : 'primary.main',
                            borderColor: copySuccess ? 'success.main' : 'primary.main',
                          }}
                        >
                          {copySuccess ? "Copied!" : "Copy Token"}
                        </Button>
                      </Box>

                      <Paper
                        elevation={0}
                        variant="outlined"
                        sx={{
                          backgroundColor: "#f4f4f4",
                          p: 2,
                          borderRadius: 2,
                          maxHeight: 150,
                          overflowY: "auto",
                          overflowWrap: "break-word",
                          wordBreak: "break-all"
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: "pre-wrap",
                            fontFamily: "monospace",
                            fontSize: '0.75rem'
                          }}
                        >
                          {currentAccessToken}
                        </Typography>
                      </Paper>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Token Info
                        </Typography>

                        {currentAccessToken && (
                          <Box sx={{ mt: 1 }}>
                            <Grid container spacing={1}>
                              <Grid item xs={4}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Issued to:
                                </Typography>
                              </Grid>
                              <Grid item xs={8}>
                                <Typography variant="caption">
                                  {decodeJwt(currentAccessToken)?.name || decodeJwt(currentAccessToken)?.upn || "Unknown"}
                                </Typography>
                              </Grid>

                              <Grid item xs={4}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Expires:
                                </Typography>
                              </Grid>
                              <Grid item xs={8}>
                                <Typography variant="caption">
                                  {decodeJwt(currentAccessToken)?.exp
                                    ? new Date(decodeJwt(currentAccessToken).exp * 1000).toLocaleString()
                                    : "Unknown"}
                                </Typography>
                              </Grid>

                              <Grid item xs={4}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Scopes:
                                </Typography>
                              </Grid>
                              <Grid item xs={8}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {decodeJwt(currentAccessToken)?.scp || "None"}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {errorMessage && (
                    <Alert
                      severity="error"
                      sx={{
                        borderRadius: 2,
                        flex: 1,
                        mr: 2
                      }}
                    >
                      {errorMessage}
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    onClick={handleRunQuery}
                    startIcon={<SendIcon />}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                      backgroundColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#1565c0',
                        boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)'
                      }
                    }}
                  >
                    Run Query
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Response Viewer */}
        <Grid item xs={12}>
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Typography
              variant="h6"
              sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid rgba(0,0,0,0.12)',
                backgroundColor: '#f9f9f9',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CodeIcon sx={{ mr: 1, color: 'primary.main' }} />
                Response
              </Box>
              
              {response && (
                <Tooltip title="Copy response">
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopyToClipboard(response)}
                    sx={{ color: copySuccess ? 'success.main' : 'action.active' }}
                  >
                    {copySuccess ? <DoneIcon /> : <ContentCopyIcon />}
                  </IconButton>
                </Tooltip>
              )}
            </Typography>
            
            <Box sx={{ p: 2 }}>
              {!response ? (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 300, // Maintain reasonable height
                    backgroundColor: '#f9f9f9',
                    borderRadius: 2,
                    p: 4,
                    border: '1px dashed rgba(0,0,0,0.12)'
                  }}
                >
                  <HttpIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                    No response yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    Click the "Run Query" button to execute your request and see results here
                  </Typography>
                </Box>
              ) : (
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    backgroundColor: "#f4f4f4",
                    p: 3,
                    borderRadius: 2,
                    height: 400, // Increased height for better viewing
                    maxHeight: 'none', // Remove max height constraint
                    overflowY: "auto"
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ 
                      whiteSpace: "pre-wrap", 
                      fontFamily: "monospace",
                      fontSize: '0.875rem'
                    }}
                  >
                    {response}
                  </Typography>
                </Paper>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

      {/* Info Box */}
      <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#f9f9f9', mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">About Microsoft Graph Explorer</Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(232, 245, 233, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#2e7d32' }}>
                  Common Endpoints
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • <strong>/me</strong> - Get your profile
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • <strong>/me/messages</strong> - Get your emails
                </Typography>
                <Typography variant="body2">
                  • <strong>/users</strong> - List users (admin)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(227, 242, 253, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1976d2' }}>
                  Request Tips
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Use <strong>$select</strong> to limit returned fields
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Use <strong>$filter</strong> to narrow results
                </Typography>
                <Typography variant="body2">
                  • Add <strong>"ConsistencyLevel": "eventual"</strong> header for $count
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255, 243, 224, 0.5)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#e65100' }}>
                  Permissions
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Each endpoint needs specific OAuth scopes
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Some operations require admin consent
                </Typography>
                <Typography variant="body2">
                  • Check the Permissions tab if you get 401/403 errors
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
}

export default GraphExplorer;
